/** @file Create-bone tool math: setup x, y, rotation and length from a head and tail dragged in world. */
import { affine } from '@pose2d/math/affine';
import { angle } from '@pose2d/math/angle';
import { trig } from '@pose2d/math/trig';
import { vec2 } from '@pose2d/math/vec2';
import type { Affine, Vec2 } from '@pose2d/math/types';
import { invertParent } from './_internal/invertParent';

/** Setup values of the new bone (scale 1, no shear, inherit normal). */
export interface BoneFromPoints {
  x: number;
  y: number;
  rotation: number;
  length: number;
}

const IDENTITY: Affine = [1, 0, 0, 1, 0, 0];

/**
 * Setup that puts the new bone's origin at `head` and its tip at `tail` (world) under `parent` (null = root); a
 * degenerate parent (|det| < 1e-12) inverts its translation only, so the result stays finite (R4-03).
 */
export function boneFromPoints(parent: Affine | null, head: Vec2, tail: Vec2): BoneFromPoints {
  const inv = parent === null ? IDENTITY : invertParent(new Float64Array(6), parent);
  const h = affine.apply(inv, head[0], head[1], [0, 0]);
  const d = affine.applyVec(inv, tail[0] - head[0], tail[1] - head[1], [0, 0]);
  return { x: h[0], y: h[1], rotation: angle.radToDeg(trig.atan2(d[1], d[0])), length: vec2.len(d) };
}
